import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import '../FundingAccountPage/fundingAccount.css';
import '../AssetOverview/assetOverview.css';
import AuthService from '../../../api/services/AuthService';
import WalletOverviewSkeleton from '../../../customComponents/WalletOverviewSkeleton';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ProfileContext } from '../../../context/ProfileProvider';
import { getWalletPageLabel } from '../../../utils/walletTypeLabels';
import {
  extractWalletTypesArrayFromResponse,
  normalizeWalletTypesList,
  WALLET_ROUTE_SLUGS,
} from '../../../utils/walletTypesNormalize';
import { normalizeEstimatedPortfolioData } from '../../../utils/normalizeEstimatedPortfolio';
import WalletFundsDataRow from './WalletFundsDataRow';
import WalletRowPopup from './WalletRowPopup';
import WalletTransferModals from './WalletTransferModals';
import { useWalletTransfer } from './useWalletTransfer';
import {
  WalletTotalsSpotSection,
  WalletTotalsUnifiedSection,
} from './WalletTotalsSections';
import {
  P2P_SWAP_EARNING_WALLET_TYPES,
  preferredAmountOrZero,
  usdtEstimateOrZero,
} from './walletPageHelpers';
import { USER_WALLET_REFRESH } from '../Order/depositDeclarationModel';

/**
 * Unified wallet chrome + optional spot layout via `surface` prop (`spot` = `/wallet/spot`, `unified` = main, p2p, swap…).
 */
const WalletPage = ({ surface = "unified" }) => {
  const isSpotSurface = surface === "spot";
  const { walletType } = useParams();
  const navigate = useNavigate();
  const {
    setCurrentPage,
    walletTypes: ctxWalletTypes,
    fetchWalletTypes,
  } = useContext(ProfileContext);

  // State declarations
  const [portfolioData, setPortfolioData] = useState({});
  const [fundData, setFundData] = useState([]);
  const [showBalance, setShowBalance] = useState(true);
  const [hideAssets, setHideAssets] = useState(false);
  const [search, setSearch] = useState("");
  const [searchActive, setSearchActive] = useState(false);
  const searchInputRef = useRef(null);
  const searchContainerRef = useRef(null);

  const toggleSearch = useCallback(() => {
    setSearchActive((v) => !v);
  }, []);

  useEffect(() => {
    if (!searchActive) return;
    const t = window.setTimeout(() => searchInputRef.current?.focus(), 320);
    return () => window.clearTimeout(t);
  }, [searchActive]);

  useEffect(() => {
    if (!searchActive) return;
    const onKey = (e) => {
      if (e.key === "Escape") setSearchActive(false);
    };
    const onClickOutside = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setSearchActive(false);
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClickOutside);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClickOutside);
    };
  }, [searchActive]);
  const [walletRowPopup, setWalletRowPopup] = useState(null);
  const [isValidWallet, setIsValidWallet] = useState(null); // null = loading, true/false = validated
  const [walletBootstrapComplete, setWalletBootstrapComplete] = useState(false);
  const [spotPnlData, setSpotPnlData] = useState(null);
  const [spotPnlLoading, setSpotPnlLoading] = useState(isSpotSurface);
  /**
   * Current route slug (normalized). Used to drop async results after navigating away vs an epoch —
   * React Strict Mode mounts twice and would bump an epoch twice, falsely discarding a valid portfolio payload.
   */
  const activeWalletSlugRef = useRef("");

  const useSpotMainSkeleton = isSpotSurface || walletType === 'main';

  // Normalize route slug once per paint for async staleness guards (Strict Mode-safe).
  activeWalletSlugRef.current = String(walletType || "").trim().toLowerCase();

  const walletLabel = useMemo(() => getWalletPageLabel(walletType), [walletType]);

  // Safely round number up to 8 decimals, return "0.00" if <= 0 or invalid
  const safeRound = useCallback((value, decimals = 8) => {
    const num = parseFloat(value);
    if (isNaN(num) || num <= 0) {
      return "0.00";
    }
    return parseFloat(num.toFixed(decimals));
  }, []);

  const portfolioUsdtEstimate = useCallback((p) => {
    return (
      p?.estimated_total_usdt ??
      p?.dollarPrice ??
      p?.estimatedTotalUsdt ??
      p?.estimated_total ??
      p?.total_usdt
    );
  }, []);

  /** Main header: user preferred currency amount (API normalized in ProfileProvider when missing) */
  const portfolioPreferredAmount = useCallback(
    (p) => {
      const usdt = portfolioUsdtEstimate(p);
      const pref = p?.estimated_total_preferred ?? p?.estimatedTotalPreferred;
      return pref !== undefined && pref !== null && pref !== "" ? pref : usdt;
    },
    [portfolioUsdtEstimate]
  );

  const portfolioPreferredCurrency = useCallback((p) => {
    return (
      p?.currency_prefrence ??
      p?.currency_preference ??
      p?.preferred_currency ??
      p?.Currency ??
      "USD"
    );
  }, []);

  /** When portfolio API is empty/error, totals still render as numeric 0 (no error banners). */
  const portfolioPreferredAmountOrZero = useCallback(
    (p) => preferredAmountOrZero(portfolioPreferredAmount, p),
    [portfolioPreferredAmount]
  );

  const portfolioUsdtEstimateOrZero = useCallback(
    (p) => usdtEstimateOrZero(portfolioUsdtEstimate, p),
    [portfolioUsdtEstimate]
  );

  const totalWalletQty = useCallback((item) => {
    return (parseFloat(item?.balance) || 0) + (parseFloat(item?.locked_balance) || 0);
  }, []);

  const approxUsdDisplay = useCallback(
    (item) => {
      const raw = item?.dollar_price ?? item?.dollarPrice ?? item?.usd_value ?? item?.estimated_usd;
      const n = parseFloat(raw);
      if (!Number.isNaN(n) && n >= 0) return safeRound(n, 2);
      const sym = (item?.short_name || "").toUpperCase();
      const stable = ["USDT", "USDC", "BUSD", "DAI", "TUSD", "USDP"].includes(sym);
      const t = totalWalletQty(item);
      if (stable && t >= 0) return safeRound(t, 2);
      return null;
    },
    [safeRound, totalWalletQty]
  );

  /** Fiat line from spot `price` (USD per coin) × amount; `"N/A"` when price missing/invalid */
  const usdApproxFromPrice = useCallback(
    (amount, item) => {
      const sym = String(item?.short_name || "").trim().toUpperCase();
      const cur = String(item?.currency || "").trim().toUpperCase();
      if (sym === "USDT" || cur === "USDT") {
        const amt = parseFloat(amount) || 0;
        return `≈ $${safeRound(amt, 2)}`;
      }
      const raw = item?.price;
      const rawStr =
        raw === undefined || raw === null ? "" : String(raw).trim();
      if (rawStr === "" || rawStr.toUpperCase() === "N/A") {
        return "N/A";
      }
      const p = parseFloat(rawStr);
      if (Number.isNaN(p) || p <= 0) {
        return "N/A";
      }
      const amt = parseFloat(amount) || 0;
      return `≈ $${safeRound(amt * p, 2)}`;
    },
    [safeRound]
  );

  const approxUsdLine = useCallback(
    (item) => {
      const v = approxUsdDisplay(item);
      if (v != null) return `≈ $${v}`;
      const fromPrice = usdApproxFromPrice(totalWalletQty(item), item);
      return fromPrice !== "N/A" ? fromPrice : "≈ —";
    },
    [approxUsdDisplay, usdApproxFromPrice, totalWalletQty]
  );

  /** Spot/market USD per unit from API `price` (same rules as fiat calc); `$X` / `N/A` */
  const spotUsdPriceLabel = useCallback(
    (item) => {
      const rawStr =
        item?.price === undefined || item?.price === null
          ? ""
          : String(item.price).trim();
      if (rawStr !== "" && rawStr.toUpperCase() !== "N/A") {
        const p = parseFloat(rawStr);
        if (!Number.isNaN(p) && p > 0) {
          return `$${safeRound(p, 5)}`;
        }
      }
      const sym = String(item?.short_name || "").trim().toUpperCase();
      const cur = String(item?.currency || "").trim().toUpperCase();
      if (sym === "USDT" || cur === "USDT") {
        return `$${safeRound(1, 2)}`;
      }
      return "N/A";
    },
    [safeRound]
  );

  // API Handlers
  const handleUserFunds = useCallback(async (type, slugAtRequest = null) => {
    const expected =
      slugAtRequest != null ? String(slugAtRequest).trim().toLowerCase() : null;
    try {
      const result = await AuthService.getUserfunds(type);
      if (expected != null && activeWalletSlugRef.current !== expected) {
        return null;
      }
      if (result?.success) {
        const wallets = result?.data || [];
        setFundData(wallets);
        return wallets;
      }
      setFundData([]);
    } catch {
      if (expected == null || activeWalletSlugRef.current === expected) {
        setFundData([]);
      }
    }
    return null;
  }, []);

  const fetchPortfolio = useCallback(
    async () => {
      const enteredSlug = String(walletType || "").trim().toLowerCase();

      const stillOnThisWallet = () => activeWalletSlugRef.current === enteredSlug;

      if (!enteredSlug) {
        if (stillOnThisWallet()) setPortfolioData({});
        return;
      }

      try {
        const result = await AuthService.estimatedPortfolio(enteredSlug);
        if (!stillOnThisWallet()) return;
        if (result?.success && result?.data != null && typeof result.data === "object") {
          setPortfolioData(normalizeEstimatedPortfolioData(result.data, enteredSlug));
        } else {
          setPortfolioData({});
        }
      } catch {
        if (stillOnThisWallet()) setPortfolioData({});
      }
    },
    [walletType]
  );

  const fetchSpotPnl = useCallback(async () => {
    if (!isSpotSurface) return;
    setSpotPnlLoading(true);
    try {
      const res = await AuthService.spotMePnl({ period: "24h" });
      if (res?.success && res.data) {
        setSpotPnlData(res.data);
      } else {
        setSpotPnlData(null);
      }
    } catch {
      setSpotPnlData(null);
    } finally {
      setSpotPnlLoading(false);
    }
  }, [isSpotSurface]);

  const {
    fromWalletType: transferFrom,
    setFromWalletType: setTransferFrom,
    toWalletType: transferTo,
    setToWalletType: setTransferTo,
    availableWalletTypes: transferWalletTypes,
    walletLabelsMap: transferWalletLabels,
    selectedCurrency: transferCurrency,
    setSelectedCurrency: setTransferCurrency,
    transferAmount,
    setTransferAmount,
    transferable,
    balanceLoading,
    sourceHint,
    handleInterchangeWallet,
    handleTransfer,
    currencyData: transferCurrencyData,
    fiveDecimal: transferFiveDecimal,
    marginInvolved,
    marginPairs,
    selectedMarginPair,
    setSelectedMarginPair,
    marginAssetType,
    setMarginAssetType,
    marginPairDropOpen,
    setMarginPairDropOpen,
    setAvailableWalletTypes: setTransferWalletTypes,
    setWalletLabelsMap: setTransferWalletLabels,
    openTransferModal: openWalletTransferModal,
  } = useWalletTransfer({
    initialFrom: walletType || "spot",
    loadWalletTypesOnMount: false,
    onTransferSuccess: async () => {
      await fetchPortfolio();
      await handleUserFunds(walletType);
    },
  });

  const getWalletTypes = useCallback(async () => {
    try {
      // Prefer context-cached wallet types (fetched once after login).
      let rawList = Array.isArray(ctxWalletTypes) ? [...ctxWalletTypes] : [];

      // If not available in context, fetch once via context helper.
      if (!rawList.length) {
        const fetched = await fetchWalletTypes?.();
        rawList = Array.isArray(fetched) ? [...fetched] : [];
      }

      // Final fallback (should be rare): direct API.
      if (!rawList.length) {
        const result = await AuthService.availableWalletTypes();
        if (result?.success) {
          rawList = extractWalletTypesArrayFromResponse(result?.data);
        }
      }

      const types = normalizeWalletTypesList(extractWalletTypesArrayFromResponse(rawList));
      const labelsMap = {};
      rawList.forEach((w) => {
        if (w && typeof w === "object" && w.type) labelsMap[w.type] = w.label || w.type;
      });
      setTransferWalletTypes(types);
      setTransferWalletLabels(labelsMap);

      const wt = String(walletType || "").trim().toLowerCase();
      const inApiList = types.includes(wt);
      const isRoutableSlug = WALLET_ROUTE_SLUGS.has(wt);
      const isValid = inApiList || isRoutableSlug;
      setIsValidWallet(isValid);

      if (!isValid) {
        navigate('/user_profile/asset_overview');
        return;
      }

      const defaultTo = types.find(t => t !== wt) || "";
      setTransferFrom(wt);
      setTransferTo(defaultTo);
    } catch {
      setIsValidWallet(false);
      navigate('/user_profile/asset_overview');
    }
  }, [walletType, navigate, ctxWalletTypes, fetchWalletTypes, setTransferWalletTypes, setTransferWalletLabels, setTransferFrom, setTransferTo]);

  // Sort and filter logic - currencies with balance on top
  const sortedFundData = useMemo(() => {
    const data = [...(fundData || [])];

    // Sort: non-zero total balance on top, then by total balance descending
    return data.sort((a, b) => {
      const totalA = (parseFloat(a?.balance) || 0) + (parseFloat(a?.locked_balance) || 0);
      const totalB = (parseFloat(b?.balance) || 0) + (parseFloat(b?.locked_balance) || 0);

      // Non-zero balances first
      if (totalA > 0 && totalB === 0) return -1;
      if (totalA === 0 && totalB > 0) return 1;

      // Both have balance or both are zero - sort by total balance descending
      return totalB - totalA;
    });
  }, [fundData]);

  const finalFundData = sortedFundData.filter((item) =>
  (item?.short_name?.toLowerCase()?.includes(search?.toLowerCase()) ||
    item?.currency?.toLowerCase()?.includes(search?.toLowerCase()))
  );

  const filteredCoinList = hideAssets
    ? finalFundData.filter(item => ((parseFloat(item?.balance) || 0) + (parseFloat(item?.locked_balance) || 0)) > 0)
    : finalFundData;

  /** P2P / Swap / Earning (unified surface): API often returns full coin list with zeros — show empty until balance. */
  const walletTableRows = useMemo(() => {
    if (isSpotSurface) return filteredCoinList;
    if (!P2P_SWAP_EARNING_WALLET_TYPES.includes(walletType)) return filteredCoinList;
    return (filteredCoinList || []).filter((item) => totalWalletQty(item) > 0);
  }, [walletType, filteredCoinList, totalWalletQty, isSpotSurface]);

  const handleSpotTransferClick = useCallback(
    (item) => (e) => {
      e.preventDefault();
      openWalletTransferModal({
        from: walletType,
        to: transferWalletTypes.find((t) => t !== walletType) || "main",
        currency: item,
      });
    },
    [walletType, transferWalletTypes, openWalletTransferModal]
  );

  /** Mobile row sheet closes before Bootstrap can open #walletTransferModal from data-bs-toggle — open after paint. */
  const showWalletTransferModalImperative = useCallback(() => {
    if (typeof window === "undefined") return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        try {
          window.$?.("#walletTransferModal")?.modal?.("show");
        } catch {
          // Bootstrap/jQuery not available
        }
      });
    });
  }, []);

  useLayoutEffect(() => {
    setPortfolioData({});
    setFundData([]);
    setSpotPnlData(null);
    if (isSpotSurface) setSpotPnlLoading(true);
    if (useSpotMainSkeleton) setWalletBootstrapComplete(false);
  }, [walletType, useSpotMainSkeleton, surface, isSpotSurface]);

  // Effects
  useEffect(() => {
    // Set current page for sidebar
    setCurrentPage(walletLabel);

    const slugAtMount = activeWalletSlugRef.current;

    const initData = async () => {
      const skipInitLoader = isSpotSurface || walletType === 'main';
      // if (!skipInitLoader) LoaderHelper.loaderStatus(true);
      try {
        // Only validate/fetch wallet types when a walletType param is present.
        if (walletType) {
          await getWalletTypes(); // validates the wallet type
        }
        if (activeWalletSlugRef.current !== slugAtMount) return;
        await Promise.all([
          fetchPortfolio(),
          handleUserFunds(walletType, slugAtMount),
        ]);
      } finally {
        // if (!skipInitLoader) LoaderHelper.loaderStatus(false);
        if (activeWalletSlugRef.current === slugAtMount && skipInitLoader) {
          setWalletBootstrapComplete(true);
        }
      }
    };

    initData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletType]);

  useEffect(() => {
    const onWalletRefresh = () => {
      const slug = activeWalletSlugRef.current;
      void handleUserFunds(walletType, slug);
    };
    window.addEventListener(USER_WALLET_REFRESH, onWalletRefresh);
    return () => window.removeEventListener(USER_WALLET_REFRESH, onWalletRefresh);
  }, [handleUserFunds, walletType]);

  useEffect(() => {
    if (walletType) setTransferFrom(walletType);
  }, [walletType, setTransferFrom]);

  useEffect(() => {
    if (!isSpotSurface) return;
    fetchSpotPnl();
  }, [isSpotSurface, fetchSpotPnl]);

  useEffect(() => {
    if (!isSpotSurface) return;
    const id = setInterval(() => fetchSpotPnl(), 45000);
    return () => clearInterval(id);
  }, [isSpotSurface, fetchSpotPnl]);

  useEffect(() => {
    if (!walletRowPopup) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") setWalletRowPopup(null);
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [walletRowPopup]);

  /** Keep mobile row sheet balances in sync after internal transfer refreshes fundData */
  useEffect(() => {
    setWalletRowPopup((prev) => {
      if (!prev?.item?.currency_id) return prev;
      const fresh = fundData?.find((r) => String(r.currency_id) === String(prev.item.currency_id));
      if (!fresh) return prev;
      const pb = Number(prev.item.balance),
        pl = Number(prev.item.locked_balance),
        fb = Number(fresh.balance),
        fl = Number(fresh.locked_balance);
      if (pb === fb && pl === fl) return prev;
      return { ...prev, item: { ...prev.item, ...fresh } };
    });
  }, [fundData]);

  // Render action buttons based on wallet type (optional closePopup for row-detail modal)
  const renderActionButtons = (item, closePopup) => {
    const fromSheet = typeof closePopup === "function";
    const bsTransferModalProps = fromSheet
      ? {}
      : { "data-bs-toggle": "modal", "data-bs-target": "#walletTransferModal" };

    const openTransferModal = (e) => {
      e.preventDefault();
      closePopup?.();
      openWalletTransferModal({
        from: walletType,
        to: transferWalletTypes.find((t) => t !== walletType) || "main",
        currency: item,
      });
      showWalletTransferModalImperative();
    };


    switch (walletType) {
      case 'spot':
        return (
          <>
            {/* <Link
              to="/convert"
              className="btn-link text-success"
              onClick={() => {
                closePopup?.();
              }}
            >
              Convert
            </Link> */}
            <Link to={`/trade/${item?.short_name}_USDT`} className="btn-link text-success" onClick={() => closePopup?.()}>
              Trade
            </Link>
            <a
              href="#/"
              className="btn-link text-success"
              {...bsTransferModalProps}
              onClick={openTransferModal}
            >
              Transfer
            </a>
          </>
        );

      case 'p2p':
        return (
          <>
            <a
              href="#/"
              className="btn-link text-white"
              {...bsTransferModalProps}
              onClick={openTransferModal}
            >
              Transfer
            </a>
            <Link to="/p2p-dashboard" className="btn-link text-white" onClick={() => closePopup?.()}>
              P2P Trade
            </Link>
          </>
        );

      case 'earning':
        return (
          <>
            <a
              href="#/"
              className="btn-link text-white"
              {...bsTransferModalProps}
              onClick={openTransferModal}
            >
              Transfer
            </a>
            {/* <Link to="/earning" className="btn-link text-white" onClick={() => closePopup?.()}>
              Earning
            </Link> */}
          </>
        );

      case 'swap':
        return (
          <>
            <a
              href="#/"
              className="btn-link text-white"
              {...bsTransferModalProps}
              onClick={openTransferModal}
            >
              Transfer
            </a>
            <Link to="/convert" className="btn-link text-white" onClick={() => closePopup?.()}>
              Swap
            </Link>
          </>
        );

      case 'main':
        return (
          <>
            <a
              href="#/"
              className="btn-link text-white"
              {...bsTransferModalProps}
              onClick={openTransferModal}
            >
              Transfer
            </a>
            <Link to="/asset_managemnet/deposit" className="btn-link text-white" onClick={() => closePopup?.()}>
              Deposit
            </Link>
            <Link to="/asset_managemnet/withdraw" className="btn-link text-white" onClick={() => closePopup?.()}>
              Withdraw
            </Link>
          </>
        );

      case 'futures':
        return (
          <>
            <a
              href="#/"
              className="btn-link text-white"
              {...bsTransferModalProps}
              onClick={openTransferModal}
            >
              Transfer
            </a>
            <Link to="/usd_futures/header" className="btn-link text-white" onClick={() => closePopup?.()}>
              Futures
            </Link>
          </>
        );

      default:
        return (
          <a
            href="#/"
            className="btn-link text-white"
            {...bsTransferModalProps}
            onClick={openTransferModal}
          >
            Transfer
          </a>
        );
    }
  };

  /** Top header actions — same button classes as Asset Overview; varies by wallet route */
  const renderWalletHeaderActions = () => {
    const transferBtn = (
      <button
        type="button"
        className="btn btn-outline-custom px-4"
        data-bs-toggle="modal"
        data-bs-target="#walletTransferModal"
        onClick={(e) => {
          e.preventDefault();
          openWalletTransferModal({
            from: walletType,
            to: transferWalletTypes.find((t) => t !== walletType) || "main",
            currency: fundData[0] || {},
          });
          showWalletTransferModalImperative();
        }}
      >
        Transfer
      </button>
    );

    switch (walletType) {
      case "main":
        return (
          <>
            <button type="button" className="btn btn-deposit px-4" onClick={() => navigate("/asset_managemnet/deposit")}>
              Deposit
            </button>
            <button type="button" className="btn btn-outline-custom px-4" onClick={() => navigate("/asset_managemnet/withdraw")}>
              Withdraw
            </button>
            {transferBtn}
          </>
        );
      case "p2p":
        return (
          <>
            <button type="button" className="btn btn-deposit px-4" onClick={() => navigate("/p2p-dashboard")}>
              P2P Trade
            </button>
            {transferBtn}
          </>
        );
      case "swap":
        return (
          <>
            <button type="button" className="btn btn-deposit px-4" onClick={() => navigate("/convert")}>
              Swap
            </button>
            {transferBtn}
          </>
        );
      case "earning":
        return (
          <>
            {/* <button type="button" className="btn btn-deposit px-4" onClick={() => navigate("/earning")}>
              Earning
            </button> */}
            {transferBtn}
          </>
        );
      case "futures":
        return (
          <>
            <button type="button" className="btn btn-deposit px-4" onClick={() => navigate("/usd_futures/header")}>
              Futures
            </button>
            {transferBtn}
          </>
        );
      default:
        return transferBtn;
    }
  };

  if (
    useSpotMainSkeleton &&
    (isValidWallet === null || (isValidWallet === true && !walletBootstrapComplete))
  ) {
    return (
      <WalletOverviewSkeleton variant={isSpotSurface ? 'spot-wallet' : 'main-wallet'} />
    );
  }

  // Validating wallet type / first paint: show same centered empty art as no-data (avoids blank screen)
  if (isValidWallet === null) {
    return (
      <div className="dashboard_right funding-account agce-wallet-page agce-wallet-page--initial">
        <div className="agce-wallet-empty-canvas" role="status" aria-live="polite">
          <div className="no-data-wrapper">
            <div className="no_data_vector">
              <img src="/images/no_data_vector.svg" className="dark_img" alt="" />
              <img src="/images/no_data_vector_light.png" className="light_img" alt="" />
            </div>
            <p className="agce-wallet-empty-canvas__hint mb-0 mt-3 text-muted small">Loading wallet…</p>
          </div>
        </div>
      </div>
    );
  }

  if (isValidWallet === false) {
    return null; // Will redirect
  }



  return (
    <>
      {isSpotSurface ? (
        <div className="dashboard_right funding-account wallet-page--ao-popup">
          <div className="funding-account__header border-none">
            <div>
              <h1 className="funding-account__title">Spot Wallet Balance</h1>
            </div>
            <div className="funding-account__header-actions">
              <button
                type="button"
                className="funding-account__pill funding-account__pill--primary"
                onClick={() => navigate("/asset_managemnet/deposit")}
              >
                Deposit
              </button>
              <button type="button" className="funding-account__pill" onClick={() => navigate("/asset_managemnet/deposit")}>
                Buy Crypto
              </button>
              <button
                type="button"
                className="funding-account__pill"
                data-bs-toggle="modal"
                data-bs-target="#walletTransferModal"
                onClick={(e) => {
                  e.preventDefault();
                  openWalletTransferModal({
                    from: walletType,
                    to: transferWalletTypes.find((t) => t !== walletType) || "main",
                    currency: fundData[0] || {},
                  });
                }}
              >
                Transfer
              </button>
              <button type="button" className="funding-account__pill" onClick={() => navigate("/user_profile/wallet/spot/pnl-analysis")}>
                PNL Analysis
              </button>
              <button type="button" className="funding-account__pill" onClick={() => navigate("/asset_managemnet/withdraw")}>
                Withdraw
              </button>
            </div>
          </div>

          <div className='estimated_balance mb-4'>

            <WalletTotalsSpotSection
              showBalance={showBalance}
              setShowBalance={setShowBalance}
              portfolioPreferredAmountOrZero={portfolioPreferredAmountOrZero}
              portfolioPreferredCurrency={portfolioPreferredCurrency}
              portfolioUsdtEstimateOrZero={portfolioUsdtEstimateOrZero}
              portfolioData={portfolioData}
              safeRound={safeRound}
              spotPnlData={spotPnlData}
              spotPnlLoading={spotPnlLoading}
            />

            <div className="funding-account__filters rightsearch">
              <div
                ref={searchContainerRef}
                className={`searchBar custom-tabs ${searchActive ? "search_active" : ""}`}
                onClick={toggleSearch}
              >
                <i className="ri-search-2-line" aria-hidden="true" />
                <input
                  ref={searchInputRef}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search"
                  type="search"
                  className="custom_search"
                  aria-label="Search assets"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <label className="funding-account__check">
                <input type="checkbox" checked={hideAssets} onChange={(e) => setHideAssets(e.target.checked)} />
                Hide 0 balances
              </label>
              {/* <label className="funding-account__check smallkcs">
              <input type="checkbox" />
              Convert small balances to KCS
            </label> */}
            </div>

            <div className="funding-account__hintbar">
              <i className="ri-information-line" aria-hidden="true" />
              <span>
                To trade tokens, click Transfer to move the assets from your Funding Account to your Trading Account.
              </span>
            </div>

          </div>

          <div className="funding-account__tablewrap">
            <table className="funding-account__table">
              <thead>
                <tr>
                  <th>Coin</th>
                  <th className="ta-r">Total Amount</th>
                  <th className="ta-r">Available</th>
                  <th className="ta-r">In Order</th>
                  <th className="ta-r">Avg. Cost Price</th>
                  <th className="ta-r">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredCoinList?.length > 0 ? (
                  filteredCoinList.map((item, index) => (
                    <WalletFundsDataRow
                      key={item?.currency_id || index}
                      item={item}
                      spotStyleActions
                      popupKind="spot"
                      onOpenMobileSheet={(kind, row) => setWalletRowPopup({ kind, item: row })}
                      safeRound={safeRound}
                      totalWalletQty={totalWalletQty}
                      approxUsdLine={approxUsdLine}
                      usdApproxFromPrice={usdApproxFromPrice}
                      spotUsdPriceLabel={spotUsdPriceLabel}
                      handleSpotTransferClick={handleSpotTransferClick}
                      renderActionButtons={renderActionButtons}
                    />
                  ))
                ) : (
                  <tr className="no-data-row no-data-row2">
                    <td colSpan={6}>
                      <div className="no-data-wrapper">
                        <div className="no_data_vector">
                          <img src="/images/no_data_vector.svg" className="dark_img" alt="no-data" />
                          <img src="/images/no_data_vector_light.png" className="light_img" alt="no-data" />
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="dashboard_right funding-account wallet-nonspot">
          <div className="overview_section">
            <div className="funding-account__header border-0 pb-0">
              <div>
                <h1 className="funding-account__title">{walletLabel}</h1>
              </div>
              <div className="d-flex gap-2 mt-3 mt-md-0 flex-wrap justify-content-end">{renderWalletHeaderActions()}</div>
            </div>

            <WalletTotalsUnifiedSection
              walletLabel={walletLabel}
              showBalance={showBalance}
              setShowBalance={setShowBalance}
              portfolioPreferredAmountOrZero={portfolioPreferredAmountOrZero}
              portfolioPreferredCurrency={portfolioPreferredCurrency}
              portfolioUsdtEstimateOrZero={portfolioUsdtEstimateOrZero}
              portfolioData={portfolioData}
              safeRound={safeRound}
            />
          </div>

          <div className="dashboard_listing_section Overview_mid">
            <div className="assets_wallets_section">
              <div className="market_section">
                <div className="coin_view_top">
                  <div className="wallet_tabs">
                    <span className="tab_btn active">{walletLabel} Assets</span>
                  </div>
                  <div className="coin_right">
                    <div className="searchBar custom-tabs">
                      <i className="ri-search-2-line"></i>
                      <input
                        type="search"
                        className="custom_search"
                        placeholder="Search Crypto"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                    </div>

                    <div className="checkbox">
                      <input type="checkbox" checked={hideAssets} onChange={() => setHideAssets((prev) => !prev)} />
                      Hide 0 Balance
                    </div>
                  </div>
                </div>

                <div className="dashboard_summary">
                  <div className="desktop_view-2">
                    <div className="funding-account__tablewrap table-responsive">
                      <table className="funding-account__table">
                        <thead>
                          <tr>
                            <th>Coin</th>
                            <th className="ta-r">Total Amount</th>
                            <th className="ta-r">Available</th>
                            <th className="ta-r">In Order</th>
                            <th className="ta-r">Avg. Cost Price</th>
                            <th className="ta-r">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {walletTableRows?.length > 0 ? (
                            walletTableRows.map((item, index) => (
                              <WalletFundsDataRow
                                key={item?.currency_id || index}
                                item={item}
                                spotStyleActions={false}
                                popupKind="list"
                                onOpenMobileSheet={(kind, row) => setWalletRowPopup({ kind, item: row })}
                                safeRound={safeRound}
                                totalWalletQty={totalWalletQty}
                                approxUsdLine={approxUsdLine}
                                usdApproxFromPrice={usdApproxFromPrice}
                                spotUsdPriceLabel={spotUsdPriceLabel}
                                handleSpotTransferClick={handleSpotTransferClick}
                                renderActionButtons={renderActionButtons}
                              />
                            ))
                          ) : (
                            <tr className="no-data-row no-data-row2">
                              <td colSpan="6">
                                <div className="no-data-wrapper">
                                  <div className="no_data_vector">
                                    <img src="/images/no_data_vector.svg" className="dark_img" alt="no-data" />
                                    <img src="/images/no_data_vector_light.png" className="light_img" alt="no-data" />
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <WalletRowPopup
        walletRowPopup={walletRowPopup}
        isSpotSurface={isSpotSurface}
        setWalletRowPopup={setWalletRowPopup}
        renderActionButtons={renderActionButtons}
        totalWalletQty={totalWalletQty}
        safeRound={safeRound}
        approxUsdLine={approxUsdLine}
        usdApproxFromPrice={usdApproxFromPrice}
        spotUsdPriceLabel={spotUsdPriceLabel}
      />

      <WalletTransferModals
        fromWalletType={transferFrom}
        setFromWalletType={setTransferFrom}
        toWalletType={transferTo}
        setToWalletType={setTransferTo}
        availableWalletTypes={transferWalletTypes}
        walletLabelsMap={transferWalletLabels}
        selectedCurrency={transferCurrency}
        setSelectedCurrency={setTransferCurrency}
        transferAmount={transferAmount}
        setTransferAmount={setTransferAmount}
        transferable={transferable}
        balanceLoading={balanceLoading}
        sourceHint={sourceHint}
        handleInterchangeWallet={handleInterchangeWallet}
        handleTransfer={handleTransfer}
        currencyData={transferCurrencyData}
        fiveDecimal={transferFiveDecimal}
        marginInvolved={marginInvolved}
        marginPairs={marginPairs}
        selectedMarginPair={selectedMarginPair}
        setSelectedMarginPair={setSelectedMarginPair}
        marginAssetType={marginAssetType}
        setMarginAssetType={setMarginAssetType}
        marginPairDropOpen={marginPairDropOpen}
        setMarginPairDropOpen={setMarginPairDropOpen}
      />
    </>
  );
};

export default WalletPage;

